import { useMemo, useState, type FormEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

/**
 * TicketBillableDialog — controlled dialog enforcing TKT-03.
 *
 * When opened, proposes flipping isBillable to its opposite. Requires a
 * non-empty reason before the Save button becomes enabled. On submit, calls
 * onSubmit with the new { isBillable, billableReason }.
 *
 * Copy pinned verbatim per plan:
 *   'Change billable status to {value}. This requires a reason for the audit log.'
 */

export interface BillableSubmitPayload {
  isBillable: boolean;
  billableReason: string;
}

interface Props {
  open: boolean;
  current: {
    isBillable: boolean;
    billableReason: string | null;
  };
  onSubmit: (payload: BillableSubmitPayload) => void;
  onCancel: () => void;
  busy?: boolean;
}

export function TicketBillableDialog({ open, current, onSubmit, onCancel, busy }: Props) {
  const [reason, setReason] = useState('');

  // The dialog always proposes flipping the current isBillable value.
  const newValue = !current.isBillable;

  const isValid = useMemo(() => reason.trim().length > 0, [reason]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    onSubmit({ isBillable: newValue, billableReason: reason.trim() });
  }

  function handleOpenChange(o: boolean) {
    if (!o) {
      setReason('');
      onCancel();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change billable status</DialogTitle>
          <DialogDescription>
            {`Change billable status to ${String(newValue)}. This requires a reason for the audit log.`}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="billable-reason">Reason</Label>
            <Input
              id="billable-reason"
              type="text"
              placeholder="Describe why this billable status is changing…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || busy}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
